import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Adresse, AdresseChangeRequest, Benachrichtigungen, EleKomChangeRequest, GrueneKarteRequest, IdentifikationRequest, NameChangeRequest, PayloadBenachrichtigungen, PayloadBoolean, PayloadIdentifikationResponse, PayloadInteger, PayloadString, PayloadStringList, WetterChangeRequest, WetterChangeZustimmungRequest, Wetterwarnung } from '@karl/entities';
import { Observable } from 'rxjs';
import { RestService } from './rest.service';

@Injectable({
  providedIn: 'root'
})
export class RestUserService extends RestService {

  constructor(protected override http: HttpClient) {
    super(http);
  }

  loginWithCredentials(email: string, password: string, reCaptchaToken: string): Observable<PayloadBoolean> {
    const body = {
      email:          email,
      password:       password,
      reCaptchaToken: reCaptchaToken
    };
    return this.post<PayloadBoolean>('/auth/login', body);
  }

  loginNVTan(email: string, password: string, tan: string, reCaptchaToken: string): Observable<PayloadBoolean> {
    const body = {
      email:          email,
      password:       password,
      reCaptchaToken: reCaptchaToken,
      tan:            tan
    } as UserPasswordTanDto;
    return this.post<PayloadBoolean>('/auth/loginnvtan', body, null, true);
  }

  startIdentifikation(body: IdentifikationRequest): Observable<PayloadIdentifikationResponse> {
    return this.put('/protected/identcode/issue', body);
  }

  requestSmsForIdentifikation(body: IdentifikationRequest): Observable<PayloadIdentifikationResponse> {
    return this.put('/protected/identcode/requestsms', body);
  }

  validateIdentifikation(body: IdentifikationRequest): Observable<PayloadIdentifikationResponse> {
    return this
      .put('/protected/identcode/validate', body);
  }

  changeAdresse(newAdr: Adresse, oldAdr: Adresse, grund: string, anmerkung: string): Observable<PayloadBoolean> {
    const body: AdresseChangeRequest = {
      adresseAlt: oldAdr,
      adresseNeu: newAdr,
      grund:      grund,
      anmerkung:  anmerkung
    };
    return this.put('/protected/change/adresse', body);
  }

  changeWetter(wetterwarnungen: Wetterwarnung[]): Observable<PayloadBoolean> {
    const body: WetterChangeRequest = {
      wetterwarnungen: wetterwarnungen
    };
    return this.put('/protected/change/wetter', body);
  }

  changeWetterZustimmung(isZustimmung: boolean): Observable<PayloadBoolean> {
    const body: WetterChangeZustimmungRequest = {
      zustimmung: isZustimmung
    };
    return this.put('/protected/change/wetterzustimmung', body);
  }

  requestGrueneKarte(
    polizzeNr: string,
    fahrzeugbezeichnung: string,
    kundenbueroNr: number,
    kundenbueroAbholung: boolean
  ): Observable<PayloadBoolean> {
    const body = {
      polizzenNr:          polizzeNr,
      fahrzeugBezeichnung: fahrzeugbezeichnung,
      kundenbueroNr:       kundenbueroNr,
      kundenbueroAbholung: kundenbueroAbholung
    } as GrueneKarteRequest;
    return this.put('/protected/karten/gruenekarte', body);
  }

  generateGrueneKarten(body: GrueneKarteRequest): Observable<PayloadStringList> {
    return this.patch('/protected/karten/gruenekarten/generatepdf/' + body.polizzenNr, body);
  }

  changeName(
    titel: string,
    vorname: string,
    nachname: string,
    grund: string,
    fileObjectdIds: string[]
  ): Observable<PayloadBoolean> {
    const body = {
      titel:         titel,
      vorname:       vorname,
      nachname:      nachname,
      grund:         grund,
      fileObjectIds: fileObjectdIds
    } as NameChangeRequest;
    return this.put('/protected/change/name', body);
  }

  sendTokenForCommunicationEmailChange(newEmail: string): Observable<PayloadBoolean> {
    const body = {
      email: newEmail
    };
    return this.put('/protected/token/communicationemail', body);
  }

  sendTokenForMobileChange(tel: string): Observable<PayloadBoolean> {
    const body = {
      mobilNr: tel
    };
    return this.put('/protected/token/mobil', body);
  }

  verifyMobileChangeToken(code: string): Observable<PayloadBoolean> {
    return this
      .put('/protected/token/mobil/verify/' + code, null);
  }

  changeElektronischeKommunikation(isAccepted: boolean): Observable<PayloadBoolean> {
    const body: EleKomChangeRequest = {
      eleKomAccepted: isAccepted
    };
    return this.put('/protected/change/elektronischekommunikation', body);
  }

  sendTokenForElectronicCommunication(newEmail: string): Observable<PayloadBoolean> {
    const body = {
      email: newEmail
    };
    return this
      .put('/protected/token/communicationemailandaccept', body);
  }

  changePassword(actualPassword: string, newPassword: string): Observable<PayloadInteger> {
    const body = {
      actualPassword: actualPassword,
      newPassword:    newPassword
    };
    return this
      .put('/protected/changepassword', body);
  }

  changeEmail(newEmail: string): Observable<PayloadBoolean> {
    const body = {
      newEmail: newEmail
    };
    return this.put('/protected/changeemail', body);
  }

  addCredential(email: string, password: string): Observable<PayloadBoolean> {
    const body = {
      email:    email,
      password: password
    };
    return this
      .put('/protected/addcredential', body);
  }

  unlinkNVCredentials(): Observable<PayloadBoolean> {
    return this
      .put('/auth/unlink/credential', {});
  }

  connectGoogle(): void {
    window.location.href = this.secured_endpoint + '/auth/connect/google';
  }

  unlinkGoogle(): Observable<PayloadBoolean> {
    return this.put('/auth/unlink/google', {});
  }

  // TODO check if needed since notifications are saved inside user too
  getBenutzerDataBenachrichtigungen(): Observable<PayloadBenachrichtigungen> {
    return this.get('/protected/benutzerdata/benachrichtigungen');
  }

  benachrichtigungGelesen(body: Benachrichtigungen): Observable<PayloadIdentifikationResponse> {
    return this.put('/protected/benachrichtigungen/gelesen', body);
  }

  idAustriaComplete(body: IdentifikationRequest): Observable<PayloadBoolean> {
    return this
      .put('/identification/idaustria/identify/complete', body);
  }
}

interface UserPasswordTanDto {
  email: string;
  password: string;
  reCaptchaToken: string;
  tan: string;
}
